/**
 * AddToProductMediaMode — Variant-A media mode (mid panel).
 *
 * Layout: KI reference slots on top (always), a channel view switch, then
 * either all channel grids stacked ('all') or a single-channel grid ('single').
 *
 * This component does NOT host its own DndContext — the context lives one
 * level up in AddToProductOverlay so sidebar drag sources and the slots here
 * share it (see PR3 part B).
 *
 * Slot count: a module-level SLOT_COUNT constant, mirroring the convention in
 * RemakeApplyMediaMode (`const SLOT_COUNT = 8`).
 *
 * Occupancy/fill state comes from `useSlotOccupancy(productId)` — TanStack
 * dedupes the underlying queries with the workspace-level call in
 * `useAddToProductDrop`, so the data is shared via the QueryClient cache.
 */

import { useMemo, useState } from 'react'
import type { ApplyChannel } from '@/components/v2/tools/amazon-listing/RemakeApply/RemakeApplyChannelPicker'
import { useProductChannelList } from '@/hooks/useProductChannelList'
import { KiReferenceSlots } from './media/KiReferenceSlots'
import { ChannelViewSwitch } from './media/ChannelViewSwitch'
import { AllChannelsView } from './media/AllChannelsView'
import { SingleChannelView } from './media/SingleChannelView'
import { MediaSizeToggle, type MediaSize } from './media/MediaSizeToggle'
import { useSlotOccupancy } from './media/useSlotOccupancy'
import { mergeChannelFills, mergeAiRef } from './media/mergeMediaFills'
import type { ChannelReorderPlans } from './media/channelReorderPlan'
import type { SlotFill } from './media/AddToProductSlotGrid'
import type { AddToProductImage, AddToProductTarget } from './types'

interface AddToProductMediaModeProps {
  productId: string
  pendingByChannelSlot?: Map<string, Map<number, AddToProductImage>>
  pendingByAiRefSlot?: Map<number, AddToProductImage>
  /**
   * Live-fix: per-channel reorder plans. When present for a channel, the plan
   * is the SOLE source of truth for that channel's slot rendering — saved
   * occupancy + per-slot pending entries are ignored for that channel. The
   * plan already encodes the final order (existing items moved + new inserts).
   */
  reorderPlans?: ChannelReorderPlans
  onDelete?: (target: AddToProductTarget) => void
}

interface MediaViewBodyProps {
  view: 'all' | 'single'
  channels: ApplyChannel[]
  mergedByChannel: Record<string, Record<number, SlotFill>>
  effectiveChannel: string
  setActiveChannel: (id: string) => void
  size: MediaSize
  onDelete?: (target: AddToProductTarget) => void
}

function MediaViewBody({
  view,
  channels,
  mergedByChannel,
  effectiveChannel,
  setActiveChannel,
  size,
  onDelete,
}: MediaViewBodyProps) {
  if (view === 'all') {
    return (
      <AllChannelsView
        channels={channels}
        filledByChannel={mergedByChannel}
        size={size}
        onDelete={onDelete}
      />
    )
  }
  return (
    <SingleChannelView
      channels={channels}
      activeId={effectiveChannel}
      onChange={setActiveChannel}
      filled={mergedByChannel[effectiveChannel] ?? {}}
      size={size}
      onDelete={onDelete}
    />
  )
}

/**
 * Memoised merge of saved slot occupancy with pending local drops. Pending
 * entries take precedence and carry `isPending: true` so the grid can render
 * an amber indicator until the user saves.
 */
function useMergedFills(
  productId: string,
  pendingByChannelSlot?: Map<string, Map<number, AddToProductImage>>,
  pendingByAiRefSlot?: Map<number, AddToProductImage>,
  reorderPlans?: ChannelReorderPlans,
) {
  const { filledByChannel, filledAiRef } = useSlotOccupancy(productId || null)
  const mergedByChannel = useMemo(
    () => mergeChannelFills(filledByChannel, pendingByChannelSlot, reorderPlans),
    [filledByChannel, pendingByChannelSlot, reorderPlans],
  )
  const mergedAiRef = useMemo(
    () => mergeAiRef(filledAiRef, pendingByAiRefSlot),
    [filledAiRef, pendingByAiRefSlot],
  )
  return { mergedByChannel, mergedAiRef }
}

interface MediaBodyProps extends MediaViewBodyProps {
  mergedAiRef: Record<number, SlotFill>
  setView: (v: 'all' | 'single') => void
  setSize: (s: MediaSize) => void
}

function MediaBody(props: MediaBodyProps) {
  const { view, setView, size, setSize, mergedAiRef, onDelete } = props
  return (
    <main className="flex-1 overflow-y-auto bg-neutral-50 dark:bg-neutral-950">
      <div className="px-6 py-4 space-y-6">
        <KiReferenceSlots filled={mergedAiRef} size={size} onDelete={onDelete} />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <ChannelViewSwitch view={view} onChange={setView} />
          <MediaSizeToggle size={size} onChange={setSize} />
        </div>
        <MediaViewBody {...props} />
      </div>
    </main>
  )
}

/**
 * Bundle the local view/channel/size state plus derived merged fills so the
 * component itself stays under the 30-line function limit. Private to this
 * module — exported only as an implementation detail.
 */
function useMediaModeState(
  productId: string,
  pendingByChannelSlot?: Map<string, Map<number, AddToProductImage>>,
  pendingByAiRefSlot?: Map<number, AddToProductImage>,
  reorderPlans?: ChannelReorderPlans,
) {
  const channels = useProductChannelList(productId)
  const [view, setView] = useState<'all' | 'single'>('all')
  const [activeChannel, setActiveChannel] = useState<string>('')
  const [size, setSize] = useState<MediaSize>('M')
  const effectiveChannel = activeChannel || channels[0]?.id || ''
  const { mergedByChannel, mergedAiRef } = useMergedFills(
    productId,
    pendingByChannelSlot,
    pendingByAiRefSlot,
    reorderPlans,
  )
  return {
    channels,
    view,
    setView,
    setActiveChannel,
    size,
    setSize,
    effectiveChannel,
    mergedByChannel,
    mergedAiRef,
  }
}

/**
 * Variant-A media mode. Merges saved slot occupancy (from backend) with
 * pending drops (local Workspace state) so the slot grids visualise both
 * states uniformly — pending entries display the dropped image with an amber
 * indicator until the user clicks Save in the BottomBar.
 */
export function AddToProductMediaMode({
  productId,
  pendingByChannelSlot,
  pendingByAiRefSlot,
  reorderPlans,
  onDelete,
}: AddToProductMediaModeProps) {
  const state = useMediaModeState(
    productId,
    pendingByChannelSlot,
    pendingByAiRefSlot,
    reorderPlans,
  )
  return <MediaBody {...state} onDelete={onDelete} />
}

